/**
 * Controlled Input with Debounced Update
 * Prevents parent re-renders on every keystroke by maintaining local state
 * and only updating parent state on blur or after debounce delay
 */

import React, { useState, useEffect, useRef } from 'react';

const ControlledInput = ({ 
  value, 
  onChange, 
  onBlur,
  debugMode = false,
  fieldName = 'unknown',
  ...props 
}) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Debug logging
  useEffect(() => {
    if (debugMode) {
      console.log(`[ControlledInput:${fieldName}] Mounted`);
    }
    return () => {
      if (debugMode) {
        console.log(`[ControlledInput:${fieldName}] Unmounted`);
      }
    };
  }, [debugMode, fieldName]);

  // Sync with parent value when it changes externally
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
      if (debugMode) {
        console.log(`[ControlledInput:${fieldName}] External value changed:`, value);
      }
    }
  }, [value, debugMode, fieldName]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    if (debugMode) {
      console.log(`[ControlledInput:${fieldName}] Local change:`, newValue);
    }

    // Debounce parent update (5 seconds for better UX)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (debugMode) {
        console.log(`[ControlledInput:${fieldName}] Debounced update to parent (5s delay):`, newValue);
      }
      onChange?.(newValue);
    }, 5000); // Increased from 1000ms to 5000ms
  };

  const handleBlur = (e) => {
    // Clear debounce timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Immediately update parent on blur
    const newValue = e.target.value;
    if (newValue !== value) {
      if (debugMode) {
        console.log(`[ControlledInput:${fieldName}] Blur update to parent:`, newValue);
      }
      onChange?.(newValue);
    }
    
    onBlur?.(e);
  };

  const handleFocus = (e) => {
    if (debugMode) {
      console.log(`[ControlledInput:${fieldName}] Focus gained`);
    }
  };

  return (
    <input
      {...props}
      ref={inputRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      inputMode="text"
    />
  );
};

export default ControlledInput;
